import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IExpandedPanel } from '../IProduct';
import { IPrpDimensionState, IPrpDimensionStateRefinement, IQueryDim } from '../IPrp';
import removeDiacritics from '../utils/removeDiacritic';

import { en, fr } from './i18n';
import OnAirButtons from './OnAirButtons';

export interface PrpFilterPanelProps {
    dimensionStates: Array<IPrpDimensionState>;
    queryDims: Array<IQueryDim> | null;
    onCloseListener: () => void;
    onFilterChangeListener: (refinement: IPrpDimensionStateRefinement) => void;
    onClearAllClickListener: () => void;
    appliedFilters: IPrpDimensionStateRefinement[];
    totalProductCount: number;
    isSearchPage: boolean;
    isOnAirPage: boolean;
    isExpandedFilter: (type: string, isExpanded: boolean) => void;
    expandedFilter: IExpandedPanel[];
    loading: boolean;
}

export interface AlgoliaResponse {
    hits: Array<{
        valueName: {
            en: string;
            fr: string;
        };
        valueId: string;
        objectId: string;
    }>;
}

export const ratingSvgMapping: Record<string, string> = {
    '0': '/App_Assets/public/Raiting_00.svg',
    '0.5': '/App_Assets/public/Raiting_05.svg',
    '1': '/App_Assets/public/Raiting_10.svg',
    '1.5': '/App_Assets/public/Raiting_15.svg',
    '2': '/App_Assets/public/Raiting_20.svg',
    '2.5': '/App_Assets/public/Raiting_25.svg',
    '3': '/App_Assets/public/Raiting_30.svg',
    '3.5': '/App_Assets/public/Raiting_35.svg',
    '4': '/App_Assets/public/Raiting_40.svg',
    '4.5': '/App_Assets/public/Raiting_45.svg',
    '5': '/App_Assets/public/Raiting_50.svg',
};

export type CategoryBreadcrumb = {
    displayName: string;
    dimensionId: string;
    urlSlug: string;
};

const PrpFilterPanel = ({
    dimensionStates,
    queryDims,
    onCloseListener,
    onFilterChangeListener,
    onClearAllClickListener,
    appliedFilters,
    totalProductCount,
    isSearchPage,
    isOnAirPage,
    isExpandedFilter,
    expandedFilter,
    loading,
}: PrpFilterPanelProps) => {
    const categoryDimensionType = 'Category';

    const { i18n, t } = useTranslation('prp-filter-panel');
    i18n.addResourceBundle('en', 'prp-filter-panel', en, true, true);
    i18n.addResourceBundle('fr', 'prp-filter-panel', fr, true, true);

    const [isDimensionExpanded, setIsDimensionExpanded] = useState<Array<IExpandedPanel>>([]);

    const [isDimensionSeeMore, setIsDimensionSeeMore] = useState<Array<boolean>>(
        new Array(dimensionStates.length).fill(false),
    );

    //const [l2, setL2] = useState<Array<CategoryBreadcrumb>>([])
    // const [l3, setL3] = useState<Array<CategoryBreadcrumb>>([]);

    const [isMobileSubPanelOpen, setIsMobileSubPanelOpen] = useState(false);
    const [isCategoryExpanded, setIsCategoryExpanded] = useState(true);
    const [brandSearch, setBrandSearch] = useState('');
    const [lastBackListItem, setLastBackListItem] = useState<CategoryBreadcrumb>({
        dimensionId: '',
        displayName: '',
        urlSlug: '',
    });

    const backList = useMemo(() => {
        const arr: Array<CategoryBreadcrumb> = [];

        if (!queryDims || queryDims.length <= 0) {
            return [];
        }

        let dim = queryDims[0];
        let i = 0; // Control maximum loop
        const maxLoopLimit = 10;

        // for (let i = 0; i < queryDims.length; i++) {
        //   arr.unshift({
        //     DimensionId: queryDims[i].DimensionId,
        //     DisplayName: queryDims[i].DisplayName,
        //   });
        // }

        while (i < maxLoopLimit) {
            const dimState = dimensionStates.filter((dimstate) =>
                dimstate.Refinements.some((ref) => ref.DimensionId === dim.DimensionId),
            );
            let urlSlug = '';
            if (dimState.length > 0) {
                urlSlug = dimState[0].Refinements.filter((ref) => ref.DimensionId === dim.DimensionId)[0].UrlSlug;
            }

            arr.unshift({
                dimensionId: dim.DimensionId,
                displayName: dim.DisplayName,
                urlSlug,
            });

            if (dim.ParentDimensions.length <= 0) {
                break;
            }

            dim = dim.ParentDimensions[0];
            i++;
        }

        const lastElement = arr.pop();
        setLastBackListItem(lastElement);

        // if (arr.length === 0) {
        //   getL2(lastElement.DimensionId);
        // }

        // if (arr.length === 1) {
        //   fetchAlgolia(lastElement.DimensionId);
        // }

        return arr;
    }, [queryDims, setLastBackListItem]);

    const dimensionsExceptCategory = useMemo(() => {
        return dimensionStates.filter((dimensionState) => {
            return dimensionState.DimensionType !== categoryDimensionType;
        });
    }, [dimensionStates]);

    useEffect(() => {
        const arr: IExpandedPanel[] = [];
        const doesShopByCategoryExists =
            dimensionStates.find((s) => s.DimensionType === categoryDimensionType) !== undefined;
        if (!doesShopByCategoryExists) {
            arr.push({ Type: 'Category', Expanded: true });
        } else {
            arr.push({ Type: 'Category', Expanded: false });
        }
        dimensionStates.forEach((dimensionState, index) => {
            if (dimensionState.DimensionType !== 'Category') {
                const filterIndex = expandedFilter.findIndex((f) => f.Type === dimensionState.DimensionType);
                if (filterIndex > -1) {
                    arr.push(expandedFilter[filterIndex]);
                } else {
                    if (!doesShopByCategoryExists && index === 0) {
                        arr.push({ Type: dimensionState.DimensionType, Expanded: true });
                    } else {
                        arr.push({ Type: dimensionState.DimensionType, Expanded: false });
                    }
                }
            }
        });
        setIsDimensionExpanded(arr);
    }, [dimensionStates, setIsDimensionExpanded, backList]);

    const isExpanded = (type: string) => {
        return isDimensionExpanded.find((it) => it.Type === type)?.Expanded || false;
    };

    const getRefinements = useCallback(
        (dimensionState: IPrpDimensionState, dimensionIndex: number) => {
            if (dimensionState.DimensionType === 'TSC_d_brand' || dimensionState.DimensionType === 'Brand') {
                if (brandSearch === '') {
                    if (isDimensionSeeMore[dimensionIndex]) {
                        return dimensionState.Refinements;
                    }

                    return dimensionState.Refinements.slice(0, dimensionState.VisibleNumber);
                }
                return dimensionState.Refinements.filter((ref) => {
                    return removeDiacritics(ref.DimensionName.toLowerCase()).includes(
                        removeDiacritics(brandSearch.toLowerCase()),
                    );
                });
            }

            if (isDimensionSeeMore[dimensionIndex]) {
                return dimensionState.Refinements;
            }
            /*
      console.log('%c dimensionState:', 'color: green; font-weight: bold;');
      console.log({ dimensionState });
      */
            return dimensionState.Refinements.slice(0, dimensionState.VisibleNumber);
        },
        [isDimensionSeeMore, brandSearch],
    );

    const getTitleFormatted = useCallback((dimensionState: IPrpDimensionState) => {
        return dimensionState.ListTitle.charAt(0).toUpperCase() + dimensionState.ListTitle.slice(1).toLowerCase();
    }, []);

    const onRefinementKeyDown = (e: React.KeyboardEvent<HTMLElement>, refinement: IPrpDimensionStateRefinement) => {
        if (e.key === 'Enter' || e.key === ' ') {
            onFilterChangeListener(refinement);
        }
    };

    const renderStandardRefinement = (dimensionState: IPrpDimensionState, refinement: IPrpDimensionStateRefinement) => {
        return (
            <li className="prp-filter-panel__filter-list__item" key={refinement.DimensionId}>
                <button onKeyDown={(e) => onRefinementKeyDown(e, refinement)}>
                    <input
                        className="prp-filter-panel__filter-list__item-checkbox"
                        type="checkbox"
                        checked={refinement.Selected}
                        onChange={() => onFilterChangeListener(refinement)}
                        id={`${dimensionState.ListTitle}-${refinement.DimensionId}`}
                    />
                    <label
                        className="prp-filter-panel__filter-list__item-label"
                        htmlFor={`${dimensionState.ListTitle}-${refinement.DimensionId}`}
                    >
                        <span className="visually-hidden">{refinement.DimensionName}</span>
                        <span className="prp-filter-panel__filter-list__item-label-text" tabIndex={-1}>
                            {refinement.DimensionName.slice(0, 32) +
                                (refinement.DimensionName.length > 32 ? '...' : '')}
                        </span>
                    </label>
                </button>
            </li>
        );
    };

    const renderReviewRefinement = (dimensionState: IPrpDimensionState, refinement: IPrpDimensionStateRefinement) => {
        const showDetails = dimensionState.Refinements.some((x) => x.Selected) === false;
        const totalReviewsCount = dimensionState.Refinements.map((ref) => ref.Count).reduce(
            (acc, curr) => acc + curr,
            0,
        );
        const percentage = Math.floor((refinement.Count * 100) / totalReviewsCount);

        return (
            <li
                className="prp-filter-panel__filter-list__item prp-filter-panel__filter-list__item--reviews"
                key={refinement.DimensionId}
            >
                <input
                    className="prp-filter-panel__filter-list__item-checkbox"
                    type="checkbox"
                    checked={refinement.Selected}
                    onChange={() => onFilterChangeListener(refinement)}
                    onKeyDown={(e) => onRefinementKeyDown(e, refinement)}
                    id={`${dimensionState.ListTitle}-${refinement.DimensionId}`}
                />
                <label
                    className="prp-filter-panel__filter-list__item-label prp-filter-panel__filter-list__item-label--reviews"
                    htmlFor={`${dimensionState.ListTitle}-${refinement.DimensionId}`}
                >
                    <span
                        className="prp-filter-panel__filter-list__item-label-raiting"
                        aria-describedby={`${dimensionState.ListTitle}-${refinement.DimensionId}-review-desc`}
                    >
                        <img
                            className="prp-filter-panel__filter-list__item-label-raiting--self"
                            src={ratingSvgMapping[refinement.DimensionName[0]]}
                            alt=""
                        />
                    </span>
                    <span
                        className="prp-filter-panel__filter-list__item-label-text visually-hidden"
                        id={`${dimensionState.ListTitle}-${refinement.DimensionId}-review-desc`}
                    >
                        {refinement.DimensionName}
                    </span>
                    {showDetails && !loading && (
                        <>
                            <span
                                className="prp-filter-panel__filter-list__item-label-text"
                                id={`${dimensionState.ListTitle}-${refinement.DimensionId}-review-count`}
                            >
                                <span tabIndex={-1}>(</span>
                                {refinement.Count}
                                <span tabIndex={-1}>)</span>
                                <span className="visually-hidden">{t('reviews')}</span>
                            </span>
                            <div className="prp-filter-panel__filter-list__item-label-range">
                                <div
                                    className="prp-filter-panel__filter-list__item-label-range__level"
                                    aria-describedby={`${dimensionState.ListTitle}-${refinement.DimensionId}-review-count`}
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                        </>
                    )}
                </label>
            </li>
        );
    };

    const getCategoryPath = (path: string, index: number, count: number, slugPath: string) => {
        if (index === count - 1) {
            if (path.lastIndexOf('/') > -1) {
                let newPath = path.substring(0, path.lastIndexOf('/'));
                if (slugPath) {
                    if (newPath.lastIndexOf('/') > -1) {
                        newPath = newPath.substring(0, newPath.lastIndexOf('/')) + '/';
                    }
                    if (newPath.lastIndexOf('/') < newPath.length - 1) {
                        newPath = newPath + '/';
                    }
                }
                return newPath;
            }
        } else if (index < count - 1) {
            let i = index;
            let newPath = path;
            while (i <= count - 1) {
                if (newPath.lastIndexOf('/') > -1) {
                    newPath = newPath.substring(0, newPath.lastIndexOf('/'));
                } else {
                    break;
                }
                i++;
            }
            if (slugPath && newPath.lastIndexOf('/') < newPath.length - 1) {
                newPath = newPath + '/';
            }
            return newPath;
        }
        return path;
    };

    const renderCategory = () => {
        const categoryDimensionType = 'Category';
        const category = dimensionStates.find((s) => s.DimensionType === categoryDimensionType);
        const doesShopByCategoryExists = category !== undefined;
        let queryString = window.location.search;
        if (queryString.length > 0) {
            let queryList = queryString.slice(1).split('&');
            queryList = queryList.filter(
                (q) => !q.includes('page=') && !q.includes('category=') && !q.includes('ic=') && !q.includes('fm='),
            );
            queryString = queryList.length > 0 ? '?' + queryList.join('&') : '';
        }
        return (
            <>
                {doesShopByCategoryExists && (
                    <div className="prp-filter-panel__blocks">
                        <button
                            className="prp-filter-panel__block-title"
                            onClick={() => setIsCategoryExpanded((prev) => !prev)}
                        >
                            <span className="prp-filter-panel__block-title__text">{t('category')}</span>
                            <div
                                className={`prp-filter-panel__block-title__icon prp-filter-panel__block-title__icon${
                                    isCategoryExpanded ? '--minus' : '--plus'
                                }`}
                            />
                        </button>

                        {isCategoryExpanded && (
                            <>
                                <ul className="prp-filter-panel__back-list">
                                    {backList.map((item, index) => (
                                        <li key={item.dimensionId}>
                                            <a
                                                href={
                                                    getCategoryPath(
                                                        window.location.pathname,
                                                        index,
                                                        backList.length,
                                                        item.urlSlug,
                                                    ) +
                                                    item.urlSlug +
                                                    queryString
                                                }
                                                className="prp-filter-panel__back-list__item"
                                            >
                                                <span className="prp-filter-panel__back-list__item-icon" />
                                                <span className="prp-filter-panel__back-list__item-text">
                                                    {item.displayName}
                                                </span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                                <div className="prp-filter-panel__category-name">{lastBackListItem.displayName}</div>
                                {/* <div>Showing {backList.length === 0 ? 'refinements' : 'l3'}</div> */}
                                <ul className="prp-filter-panel__sub-categories">
                                    {/* {backList.length === 0 && category.Refinements.map((refinement) => (
                 <li key={refinement.DimensionId} className="prp-filter-panel__sub-categories__item">
                   <a href={refinement.Url}>{refinement.DimensionName}</a>
                 </li>
               ))}
               {backList.length === 1 && l3.map((subCategory) => (
                 <li key={subCategory.DimensionId} className="prp-filter-panel__sub-categories__item">
                   <a href={baseUrl + subCategory.DimensionId}>{subCategory.DisplayName}</a>
                 </li>
               ))} */}
                                    {category.Refinements.map((refinement) => (
                                        <li
                                            key={refinement.DimensionId}
                                            className="prp-filter-panel__sub-categories__item"
                                        >
                                            <a
                                                href={
                                                    refinement.Url.includes('?')
                                                        ? refinement.Url.substring(0, refinement.Url.indexOf('?')) +
                                                          (refinement.Url.includes('?') ? '?' : '') +
                                                          refinement.Url.substring(refinement.Url.indexOf('?') + 1)
                                                              .split('&')
                                                              .filter(
                                                                  (q) =>
                                                                      !q.includes('page=') && !q.includes('category='),
                                                              )
                                                              .join('&')
                                                        : refinement.Url
                                                }
                                            >
                                                {refinement.DimensionName}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="prp-filter-panel">
            {/* Header */}
            <div className="prp-filter-panel__header">
                <button className="prp-filter-panel__header__close" onClick={onCloseListener} />
                <span className="prp-filter-panel__header__title">{t('filter')}</span>
                <button className="prp-filter-panel__header__clear" onClick={onClearAllClickListener}>
                    {t('clearAll')}
                </button>
            </div>
            {/* */}
            {/* <div className="prp-filter-panel__blocks">
        <ol className="prp-filter-panel__filter-list">
          <li className="prp-filter-panel__filter-list__item">
            <input className="prp-filter-panel__filter-list__item-checkbox" type="checkbox" name="" id="uasd-1" />
            <label className="prp-filter-panel__filter-list__item-label" htmlFor="uasd-1">
              <span className="prp-filter-panel__filter-list__item-label-text">New Arrivals</span>
            </label>
          </li>
          <li className="prp-filter-panel__filter-list__item">
            <input className="prp-filter-panel__filter-list__item-checkbox" type="checkbox" name="" id="pricasd-2" />
            <label className="prp-filter-panel__filter-list__item-label" htmlFor="pricasd-2">
              <span className="prp-filter-panel__filter-list__item-label-text">Best Sellers</span>
            </label>
          </li>
          <li className="prp-filter-panel__filter-list__item">
            <input className="prp-filter-panel__filter-list__item-checkbox" type="checkbox" name="" id="pricasd-3" />
            <label className="prp-filter-panel__filter-list__item-label" htmlFor="pricasd-3">
              <span className="prp-filter-panel__filter-list__item-label-text">Clean Beauty</span>
            </label>
          </li>
        </ol>
      </div> */}

            {isOnAirPage && <OnAirButtons className="prp-filter-panel__on-air--inside-filter-panel" />}

            {/* Category */}
            {renderCategory()}

            {/* Brand */}
            {dimensionsExceptCategory.map((dimensionState, dimensionIndex) => (
                <div className="prp-filter-panel__blocks" key={dimensionState.ListTitle}>
                    {/* Refinement group title*/}
                    <button
                        className="prp-filter-panel__block-title"
                        onClick={() => {
                            const expanded = !isExpanded(dimensionState.DimensionType);
                            isExpandedFilter(dimensionState.DimensionType, expanded);
                            setIsDimensionExpanded((prev) =>
                                prev.map((it) =>
                                    it.Type === dimensionState.DimensionType
                                        ? { Type: it.Type, Expanded: expanded }
                                        : it,
                                ),
                            );
                        }}
                        aria-label="Open"
                    >
                        <span className="prp-filter-panel__block-title__text">{getTitleFormatted(dimensionState)}</span>
                        <div
                            className={`prp-filter-panel__block-title__icon prp-filter-panel__block-title__icon${
                                isExpanded(dimensionState.DimensionType) ? '--minus' : '--plus'
                            }`}
                        />
                    </button>

                    {/* Expanded section */}
                    {isExpanded(dimensionState.DimensionType) && (
                        <div>
                            {/* If dimension is brand, add search input */}
                            {(dimensionState.DimensionType === 'TSC_d_brand' ||
                                dimensionState.DimensionType === 'Brand') && (
                                <input
                                    type="text"
                                    className="prp-filter-panel__search"
                                    placeholder={t('search')}
                                    value={brandSearch}
                                    onChange={(e) => {
                                        setBrandSearch(e.target.value);
                                    }}
                                />
                            )}
                            <div className="prp-filter-panel__ml-8">
                                <ul className="prp-filter-panel__filter-list">
                                    {getRefinements(dimensionState, dimensionIndex).map((refinement) => {
                                        const isReview =
                                            refinement.DimensionType === 'TSC_d_reviews' ||
                                            refinement.DimensionType.toLowerCase() === 'review';
                                        return isReview
                                            ? renderReviewRefinement(dimensionState, refinement)
                                            : renderStandardRefinement(dimensionState, refinement);
                                    })}
                                </ul>
                                <button
                                    className="prp-filter-panel__view-more prp-filter-panel__view-more--left-reset"
                                    onClick={() => {
                                        setIsDimensionSeeMore((prev) =>
                                            prev.map((it, i) => (i === dimensionIndex ? !it : it)),
                                        );
                                    }}
                                >
                                    <span className="prp-filter-panel__view-more-text">
                                        {dimensionState.VisibleNumber > dimensionState.Refinements.length
                                            ? ''
                                            : isDimensionSeeMore[dimensionIndex]
                                              ? dimensionState.SeeLessLinkText
                                              : dimensionState.SeeMoreLinkText}
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {/* Mobile SubPanel */}
            <div className="prp-filter-panel__mobile-subpanel">
                {appliedFilters.length > 0 && (
                    <button
                        className="prp-filter-panel__mobile-subpanel__title-button"
                        onClick={() => setIsMobileSubPanelOpen((prev) => !prev)}
                    >
                        <span className="prp-filter-panel__mobile-subpanel__title-button__text">
                            {t('filtersAdded')} ({appliedFilters.length})
                        </span>
                        {/* Close Classname -> prp-filter-panel__mobile-subpanel__title-button__icon--closed */}
                        <span
                            className={`prp-filter-panel__mobile-subpanel__title-button__icon ${
                                isMobileSubPanelOpen
                                    ? ''
                                    : 'prp-filter-panel__mobile-subpanel__title-button__icon--closed'
                            }`}
                        />
                    </button>
                )}
                {isMobileSubPanelOpen && (
                    <div className="prp-filter-panel__mobile-subpanel__buttons">
                        {appliedFilters.map((filter) => (
                            <button
                                className="prp-filter-panel__mobile-subpanel__button"
                                key={filter.DimensionId}
                                onClick={() => onFilterChangeListener(filter)}
                            >
                                {(filter.DimensionType === 'TSC_d_reviews' ||
                                    filter.DimensionType.toLowerCase() === 'reviews') && (
                                    <img
                                        className="prp-filter-panel__filter-list__item-label-raiting--self prp__applied-filters__filter__stars"
                                        src={ratingSvgMapping[filter.DimensionName[0]]}
                                        alt=""
                                    />
                                )}
                                <span>{filter.DimensionName}</span>
                            </button>
                        ))}
                        <button className="prp-filter-panel__mobile-subpanel__button" onClick={onClearAllClickListener}>
                            {t('clearAll')}
                        </button>
                    </div>
                )}
                <button
                    className="prp-filter-panel__submit-button prp-filter-panel__submit-button--panel"
                    onClick={onCloseListener}
                >
                    {`${t('view')} ${totalProductCount} ${t('products')}`}
                </button>
            </div>
        </div>
    );
};

export default PrpFilterPanel;
